import { useCallback } from 'react';
import type { Key } from 'react-aria-components';
import { Autocomplete } from '@launchpad-ui/components';

import { useSnapRegistry } from '../hooks/use-snap-registry';
import type { Command } from '../types/command';
import { parseSnapInput } from '../utils/snap';

import { CommandPaletteResults } from './command-palette-results';
import { CommandPaletteSearchField } from './command-palette-search-field';
import { useSnapPrefix } from './snap-prefix-context';

import styles from './command-palette-autocomplete.module.css';

export interface CommandPaletteAutocompleteProps {
  /** Live input value. Controlled by the parent. */
  query: string;
  onQueryChange: (next: string) => void;
  /**
   * Currently entered root, or null in the main case. Drives which
   * results subtree mounts (MainResults vs EnteredResults) and what
   * the search field's pill shows.
   */
  rootCommandId: string | null;
  /**
   * Resolved root command (looked up from the registry by the
   * parent). Passed through so the search field can render the pill
   * without re-doing the lookup. Undefined when `rootCommandId` is
   * non-null but the command isn't in the registry (transient state
   * during register/unregister races, or a programmatic openRoot to
   * an id that hasn't loaded yet -- the listbox falls back to "no
   * children" while we wait).
   */
  rootCommand: Command | undefined;
  /** Activation handler (Enter, click). */
  onAction: (id: Key) => void;
  /**
   * Called when the user closes the palette via the Escape ladder.
   * Distinct from exitRoot, which steps back to main while keeping
   * the palette open.
   */
  onClose: () => void;
  /**
   * Called when the user "backs out" from inside a root with an
   * empty query (Backspace or Escape). Parent dispatches
   * `exitRoot()`.
   */
  onExitRoot: () => void;
  /**
   * Called when the user commits a snap by typing whitespace after a
   * recognised `<prefix><snap>` (or pressing Tab on a snap suggestion).
   * Parent dispatches `openRoot(command.id, query)`.
   */
  onSnapCommit: (command: Command, query: string) => void;
}

/**
 * Outer autocomplete shell. Owns:
 *
 *   - The RAC `<Autocomplete>` boundary (controlled `inputValue`).
 *   - The `<SearchField>` (above the composition boundary; never
 *     unmounts on root switch).
 *   - Composition swap between MainResults and EnteredResults. The
 *     swap point is also the React identity boundary on root change
 *     -- no `key` prop required, no `useDeferredValue`, no Suspense.
 *   - Snap parsing (`parseSnapInput`) on input change to detect
 *     commits, plus the keyboard handler that wires Tab/Backspace/
 *     Escape into the right palette actions.
 *
 * Does not:
 *
 *   - Filter or rank commands (delegated to MainResults / EnteredResults).
 *   - Render the footer (sibling of `<Autocomplete>`, owned by the
 *     provider).
 *   - Fetch data (the palette no longer fetches; consumer hooks do).
 */
export function CommandPaletteAutocomplete({
  query,
  onQueryChange,
  rootCommandId,
  rootCommand,
  onAction,
  onClose,
  onExitRoot,
  onSnapCommit,
}: CommandPaletteAutocompleteProps) {
  const snapPrefix = useSnapPrefix();
  const snapRegistry = useSnapRegistry();

  // Snap commit detection on input change. When in main mode and the
  // input is `<prefix><snap><whitespace><rest>`, dispatch the commit
  // (openRoot to the matched command, rest as the in-root query)
  // instead of just updating the query.
  const handleQueryChange = useCallback(
    (next: string) => {
      if (rootCommandId === null) {
        const parse = parseSnapInput(next, snapPrefix, snapRegistry);
        if (parse.kind === 'commit') {
          onSnapCommit(parse.command, parse.rest);
          return;
        }
      }
      onQueryChange(next);
    },
    [rootCommandId, snapPrefix, snapRegistry, onSnapCommit, onQueryChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Backspace on empty query inside a root: express "exit root"
    // gesture. Skips the clear-query step that Escape would take.
    if (e.key === 'Backspace' && query === '' && rootCommandId !== null) {
      e.preventDefault();
      onExitRoot();
      return;
    }

    // Escape ladder. The search input is the only handler -- the
    // input retains focus across all interactions (see
    // CommandPaletteSearchField's onBlur snapback), so this handler
    // always sees the Escape keydown.
    //
    //   1. Shift+Esc                -> close palette (Raycast escape
    //                                  hatch from any depth)
    //   2. query non-empty          -> clear query
    //   3. query empty + root       -> exit root (back to main)
    //   4. query empty + main mode  -> close palette
    if (e.key === 'Escape' && !e.defaultPrevented) {
      if (e.shiftKey) {
        e.preventDefault();
        onClose();
        return;
      }
      if (query !== '') {
        e.preventDefault();
        onQueryChange('');
        return;
      }
      if (rootCommandId !== null) {
        e.preventDefault();
        onExitRoot();
        return;
      }
      e.preventDefault();
      onClose();
      return;
    }
  };

  // Capture-phase Enter: activate the row advertised by
  // `aria-activedescendant`. RAC's Autocomplete Enter path reads a
  // private `queuedActiveDescendant` ref instead. After a scope
  // exit/re-entry those two slots can disagree -- the attribute
  // points at a live option while the ref is empty -- so RAC's
  // bubble handler no-ops even though the UI looks focused. Taking
  // over in capture (and stopping propagation) makes Enter follow
  // what the user can see. When the attribute is still unset (RAC's
  // 500ms delay window after typing), we leave the event alone so
  // RAC can still activate via the queued ref.
  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.defaultPrevented) {
      return;
    }
    const activeId = e.currentTarget.getAttribute('aria-activedescendant');
    if (activeId === null || activeId === '') {
      return;
    }
    const item = document.getElementById(activeId);
    if (item === null) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    item.dispatchEvent(new PointerEvent('click', e.nativeEvent));
  };

  // Pill body: prefer the command's own snap key (the gesture the
  // user knows), fall back to the id (deterministic; matches what
  // programmatic `openRoot` calls pass in). Length-zero guard for
  // the edge case where a consumer sets `snap: ''`.
  const pillBareKey =
    rootCommand?.snap !== undefined && rootCommand.snap.length > 0 ? rootCommand.snap : rootCommand?.id;
  const pill =
    rootCommand !== undefined && pillBareKey !== undefined
      ? { key: `${snapPrefix}${pillBareKey}`, ariaLabel: `Inside ${rootCommand.label}` }
      : undefined;

  // Inside a root, the generic "Search or jump to..." placeholder is a
  // lie -- the input narrows to that root's children. Lean on the
  // root's label as the most user-relevant body.
  const placeholder =
    rootCommand !== undefined ? `Search ${rootCommand.label.toLowerCase()}...` : 'Search or jump to...';

  // RAC's built-in "focus the first row as you type" is safe to use as of
  // react-aria 3.50: it defers the pin until the (externally filtered)
  // collection commits, instead of firing synchronously inside onChange
  // against the stale collection. See the note in CommandPaletteResults.
  //
  // Key on rootCommandId so the Autocomplete remounts on scope
  // enter/exit. RAC keeps `queuedActiveDescendant`, a delayed
  // `setFocusedNodeId` timeout, and `focusedNodeId` on this boundary;
  // listbox-only remounts (in CommandPaletteResults) leave those
  // slots alive, and the delayed timeout can rewrite
  // `aria-activedescendant` after our CLEAR_FOCUS reset without
  // restoring the queued ref Enter actually reads. A fresh
  // Autocomplete drops the timeout and both focus slots together.
  return (
    <Autocomplete key={rootCommandId ?? 'main'} inputValue={query} onInputChange={handleQueryChange}>
      <div className={styles.searchField}>
        <CommandPaletteSearchField
          onKeyDown={handleKeyDown}
          onKeyDownCapture={handleKeyDownCapture}
          placeholder={placeholder}
          pill={pill}
        />
      </div>
      <CommandPaletteResults rootCommandId={rootCommandId} query={query} onAction={onAction} />
    </Autocomplete>
  );
}
